// Support Ticket sistemi
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, EmptyState, ListPageHeader } from '../components/ui';

const fmtDateTime = (s) => s ? new Date(s).toLocaleString('tr-TR') : '—';

const PRIORITY = {
  low:    { label: 'Düşük',   clr:'#64748b', bg:'rgba(100,116,139,0.10)' },
  normal: { label: 'Normal',  clr:'#2563eb', bg:'rgba(59,130,246,0.10)' },
  high:   { label: 'Yüksek',  clr:'#d97706', bg:'rgba(245,158,11,0.12)' },
  urgent: { label: 'Acil',    clr:'#dc2626', bg:'rgba(239,68,68,0.12)' },
};

const STATUS = {
  open:     { label: 'Açık',      clr:'#2563eb', bg:'rgba(59,130,246,0.10)' },
  pending:  { label: 'Beklemede', clr:'#d97706', bg:'rgba(245,158,11,0.12)' },
  resolved: { label: 'Çözüldü',   clr:'#059669', bg:'rgba(16,185,129,0.12)' },
  closed:   { label: 'Kapalı',    clr:'#64748b', bg:'rgba(100,116,139,0.10)' },
};

export default function VendorTicketsPage() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [openTicketId, setOpenTicketId] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const r = await api.get('/vendor/tickets', { params: filters });
      setList(r.data.data || []);
      setSummary(r.data.summary || {});
    } catch (e) { console.error(e); }
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    await fetchList();
    setLoading(false);
  }, [fetchList]);

  useEffect(() => { load(); }, [load]);

  // Liste için polling (15 sn) — detay modalı açıksa atlanır (detay zaten polling yapıyor)
  useEffect(() => {
    const tick = () => { if (!document.hidden && !openTicketId) fetchList(); };
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, [fetchList, openTicketId]);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Destek Talepleri"
        unit="talep"
        count={list.length}
        searchValue={filters.search}
        onSearch={(v) => setFilters(f => ({ ...f, search: v }))}
        actions={<Button size="sm" onClick={() => setCreateOpen(true)}>+ Yeni Talep</Button>}
        onRefresh={load} refreshing={loading}
      />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
                    gap: 10, padding:'0 12px 12px' }}>
        <Kpi label="Açık"      value={summary.open_count || 0}     clr="#2563eb" />
        <Kpi label="Bekleyen"  value={summary.pending_count || 0}  clr="#d97706" />
        <Kpi label="Çözüldü"   value={summary.resolved_count || 0} clr="var(--success)" />
        <Kpi label="Toplam"    value={list.length} />
      </div>

      <div style={{ padding:'0 12px 8px', display:'flex', gap: 8 }}>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="ui-input" style={{ width: 160 }}>
          <option value="">Tüm durumlar</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} className="ui-input" style={{ width: 160 }}>
          <option value="">Tüm öncelikler</option>
          {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="stocks-table-wrap">
        {loading ? <div style={{ padding: 40 }}>Yükleniyor...</div>
         : list.length === 0 ? <EmptyState title="Bu kriterlerde talep yok" />
         : (
          <table className="stocks-table">
            <thead>
              <tr>
                <th style={{ width: 100 }}>No</th>
                <th>Konu</th>
                <th>Firma / İletişim</th>
                <th style={{ width: 100 }}>Öncelik</th>
                <th style={{ width: 100 }}>Durum</th>
                <th style={{ width: 120 }}>Açılış</th>
                <th style={{ width: 60 }}>Cevap</th>
              </tr>
            </thead>
            <tbody>
              {list.map(t => {
                const p = PRIORITY[t.priority]; const s = STATUS[t.status];
                const unread = !!t.unread_for_vendor;
                return (
                  <tr key={t.id} onClick={() => setOpenTicketId(t.id)} style={{ cursor:'pointer',
                       background: unread ? 'rgba(59,130,246,0.06)' : undefined }}>
                    <td style={{ fontFamily:'monospace', fontWeight: 600 }}>
                      {unread && <span title="Yeni mesaj" style={{ display:'inline-block', width: 8, height: 8, borderRadius: '50%', background:'#2563eb', marginRight: 6 }} />}
                      {t.ticket_number}
                    </td>
                    <td><strong>{t.subject}</strong></td>
                    <td>
                      <div style={{ fontSize:'0.85rem' }}>{t.company_name || '—'}</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>{t.contact_email}</div>
                    </td>
                    <td><span style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius: 100, fontWeight:600, background: p?.bg, color: p?.clr }}>{p?.label}</span></td>
                    <td><span style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius: 100, fontWeight:600, background: s?.bg, color: s?.clr }}>{s?.label}</span></td>
                    <td style={{ fontSize:'0.78rem', color:'var(--text-tertiary)' }}>{fmtDateTime(t.created_at)}</td>
                    <td style={{ textAlign:'center', fontWeight:600 }}>{t.message_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && <NewTicketModal onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
      {openTicketId && <TicketModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} onUpdated={() => { load(); }} />}
    </div>
  );
}

function Kpi({ label, value, clr }) {
  return (
    <div style={{ background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)', borderRadius: 8,
                  padding:'12px 14px', borderTop:'3px solid ' + (clr || 'var(--border-secondary)') }}>
      <div style={{ fontSize:'0.68rem', color:'var(--text-tertiary)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize:'1.3rem', fontWeight:700, color: clr || 'var(--text-primary)', fontVariantNumeric:'tabular-nums' }}>{value}</div>
    </div>
  );
}

function NewTicketModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ contact_email: '', contact_name: '', subject: '', body: '', priority: 'normal', category: '' });
  const [busy, setBusy] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.contact_email || !form.subject) return alert('E-posta ve konu gerekli');
    setBusy(true);
    try { await api.post('/vendor/tickets', form); onSaved(); }
    catch (err) { alert(err.response?.data?.message || 'Hata'); setBusy(false); }
  };
  return (
    <Modal title="Yeni Destek Talebi" onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
          <FormField label="İletişim E-postası" required><input className="ui-input" type="email" value={form.contact_email} onChange={e => f('contact_email', e.target.value)} /></FormField>
          <FormField label="İletişim Adı"><input className="ui-input" value={form.contact_name} onChange={e => f('contact_name', e.target.value)} /></FormField>
        </div>
        <FormField label="Konu" required><input className="ui-input" value={form.subject} onChange={e => f('subject', e.target.value)} /></FormField>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
          <FormField label="Öncelik">
            <select className="ui-input" value={form.priority} onChange={e => f('priority', e.target.value)}>
              {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FormField>
          <FormField label="Kategori"><input className="ui-input" value={form.category} onChange={e => f('category', e.target.value)} placeholder="Fatura, e-fatura, kullanım..." /></FormField>
        </div>
        <FormField label="Açıklama"><textarea className="ui-input" value={form.body} onChange={e => f('body', e.target.value)} rows={5} /></FormField>
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : 'Oluştur'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function TicketModal({ ticketId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const prevMsgCount = React.useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/vendor/tickets/' + ticketId);
      if (r.data.data) {
        const msgs = r.data.data.messages || [];
        const newCount = msgs.length;
        if (prevMsgCount.current !== null && newCount > prevMsgCount.current) {
          const last = msgs[newCount - 1];
          if (last?.sender_type === 'customer') {
            setToast(`💬 Müşteri yanıtladı: ${last.sender_name || 'Müşteri'}`);
            setTimeout(() => setToast(null), 5000);
          }
        }
        prevMsgCount.current = newCount;
        setData(r.data.data);
      }
    } catch (e) { console.error(e); }
  }, [ticketId]);

  const load = useCallback(async () => {
    setLoading(true);
    await fetchData();
    setLoading(false);
  }, [fetchData]);

  useEffect(() => { load(); }, [load]);

  // Polling: detay açıkken 8 sn'de bir sessiz refresh
  useEffect(() => {
    const tick = () => { if (!document.hidden) fetchData(); };
    const iv = setInterval(tick, 8000);
    const onVis = () => { if (!document.hidden) fetchData(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchData]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post('/vendor/tickets/' + ticketId + '/reply', { body: reply, is_internal: isInternal, new_status: newStatus || null });
      setReply(''); setIsInternal(false); setNewStatus('');
      await fetchData(); onUpdated();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); }
    setBusy(false);
  };

  if (loading || !data) return <Modal title="Yükleniyor..." onClose={onClose}><div style={{ padding: 30 }}>...</div></Modal>;
  const t = data.ticket; const msgs = data.messages || [];
  const s = STATUS[t.status]; const p = PRIORITY[t.priority];

  return (
    <Modal title={`${t.ticket_number} · ${t.subject}`} onClose={onClose} size="lg">
      {toast && (
        <div style={{ position:'fixed', top: 70, right: 20, zIndex: 99999,
                      padding:'12px 18px', background:'#2563eb', color:'#fff',
                      borderRadius: 8, boxShadow:'0 4px 16px rgba(0,0,0,0.18)', fontSize: 14,
                      fontWeight: 500 }}>
          {toast}
        </div>
      )}
      <div style={{ padding: 18 }}>
        <div style={{ display:'flex', gap: 12, marginBottom: 14, fontSize:'0.82rem', flexWrap:'wrap' }}>
          <span style={{ padding:'3px 9px', borderRadius:100, fontWeight: 600, background: s?.bg, color: s?.clr }}>{s?.label}</span>
          <span style={{ padding:'3px 9px', borderRadius:100, fontWeight: 600, background: p?.bg, color: p?.clr }}>{p?.label}</span>
          {t.company_name && <span style={{ color:'var(--text-secondary)' }}>{t.company_name}</span>}
          <span style={{ color:'var(--text-tertiary)' }}>{t.contact_email}</span>
          <span style={{ color:'var(--text-tertiary)', marginLeft:'auto' }}>{fmtDateTime(t.created_at)}</span>
        </div>

        {t.body && (
          <div style={{ padding: 14, background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)', borderRadius: 6, marginBottom: 12 }}>
            <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)', marginBottom: 4 }}>🎫 İlk mesaj</div>
            <div style={{ fontSize:'0.88rem', whiteSpace:'pre-wrap' }}>{t.body}</div>
          </div>
        )}

        {/* Cevaplar */}
        <div style={{ display:'flex', flexDirection:'column', gap: 8, marginBottom: 14 }}>
          {msgs.map(m => (
            <div key={m.id} style={{ padding: 12,
                                      background: m.is_internal ? 'rgba(245,158,11,0.08)' : 'var(--bg-secondary)',
                                      border: '1px solid ' + (m.is_internal ? 'rgba(245,158,11,0.30)' : 'var(--border-secondary)'),
                                      borderRadius: 6 }}>
              <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)', marginBottom: 4 }}>
                {m.is_internal && '🔒 Dahili · '}{m.sender_name} ({m.sender_type}) · {fmtDateTime(m.created_at)}
              </div>
              <div style={{ fontSize:'0.88rem', whiteSpace:'pre-wrap' }}>{m.body}</div>
            </div>
          ))}
        </div>

        {/* Cevap formu */}
        <div style={{ borderTop:'1px solid var(--border-secondary)', paddingTop: 14 }}>
          <FormField label="Cevabınız">
            <textarea className="ui-input" value={reply} onChange={e => setReply(e.target.value)} rows={4} />
          </FormField>
          <div style={{ display:'flex', alignItems:'center', gap: 12, marginTop: 10, flexWrap:'wrap' }}>
            <label style={{ fontSize:'0.82rem', display:'inline-flex', alignItems:'center', gap: 6 }}>
              <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} /> 🔒 Dahili not
            </label>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="ui-input" style={{ width: 200 }}>
              <option value="">Durum aynı kalsın</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>→ {v.label}</option>)}
            </select>
            <div style={{ marginLeft:'auto', display:'flex', gap: 6 }}>
              <Button variant="ghost" onClick={onClose}>Kapat</Button>
              <Button variant="primary" onClick={sendReply} disabled={!reply.trim() || busy}>{busy ? '...' : '✉ Gönder'}</Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
